import React, { useRef, useState } from 'react';

import { ArrowUpOutlined, CloseOutlined, FileTextOutlined, PaperClipOutlined } from '@ant-design/icons';
import { Message, MessageList, TypingIndicator } from '@chatscope/chat-ui-kit-react';
import { css, cx } from '@emotion/css';
import {
  ChatWithDataChartArtifact,
  ChatWithDataChartDataPoint,
  ChatWithDataTableArtifact,
  ChartType,
  ChatWithDataMessageRole,
  ChatWithDataToolName,
  ChatWithDataTableFileType,
} from '@recurrency/core-api-schema/dist/chatWithData/chatWithData';
import { TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { Input, message as antdMessage, Table, Typography } from 'antd';
import MarkdownToJsx from 'markdown-to-jsx';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { colors } from 'theme/colors';
import { ZipCelXSheet } from 'zipcelx';

import { DownloadButton, DownloadFileType } from 'components/recipes/DownloadButton';

import { formatNumber } from 'utils/formatting';

import { ACCEPTED_FILE_EXTENSIONS, parseCsvFile } from './fileParser';
import { MinMaxProposalCard } from './minMaxProposal/MinMaxProposalCard';
import { ContentBlockType, ToolBlockStatus } from './types';
import type { ChatMessage, ChatWithDataFileData, ContentBlock } from './types';

const CHART_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1'];

// Compact axis ticks (e.g. 10M, 7.5M) so large values fit the axis width instead
// of overflowing and being clipped.
const formatAxisTick = (value: number) => formatNumber(value, 1, 0, { notation: 'compact' });

// Room for the rotated Y-axis label + the X-axis title so neither gets clipped.
const CHART_MARGIN = { top: 16, right: 24, bottom: 24, left: 24 };

// textAnchor: middle centers the rotated label along the axis so neither end
// runs off the plot and gets clipped.
const yAxisLabel = (value: string) => ({
  value,
  angle: -90 as const,
  position: 'insideLeft' as const,
  style: { textAnchor: 'middle' as const },
});

// Title on the left, product download button on the right of each artifact card.
const artifactHeaderStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
`;

// Build the zipcelx sheet for the artifact's download straight from the data we
// already streamed — same Excel/notification behavior as the product's tables.
const toCell = (value: string | number | boolean | null) =>
  typeof value === 'number'
    ? { value, type: 'number' as const }
    : { value: value == null ? '' : String(value), type: 'string' as const };

function tableArtifactToSheet({ columns, rows }: ChatWithDataTableArtifact): ZipCelXSheet {
  return {
    data: [columns.map((col) => ({ value: col, type: 'string' as const })), ...rows.map((row) => row.map(toCell))],
  };
}

function chartArtifactToSheet({ xKey, yKey, data }: ChatWithDataChartArtifact): ZipCelXSheet {
  return {
    data: [
      [
        { value: xKey, type: 'string' as const },
        { value: yKey, type: 'string' as const },
      ],
      ...data.map((point) => [toCell(point.x), toCell(point.y)]),
    ],
  };
}

// Shared by the table and chart artifact cards.
const artifactContainerStyles = css`
  margin-top: 12px;
  padding: 12px;
  background: ${colors.neutral[0]};
  border: 1px solid ${colors.neutral[300]};
  border-radius: 8px;
`;

const linkPrefix = '@@link:';
/** Convert <a href="url">text</a> tags into backtick-wrapped tokens so the code override can render them as links. */
function replaceAnchorTags(text: string): string {
  return text.replace(/<a\s+href="([^"]+)"[^>]*>([^<]+)<\/a>/g, (_match, url: string, label: string) => {
    const safeLabel = label.replace(/`/g, "'");
    return `\`${linkPrefix}${url}||${safeLabel}\``;
  });
}

function Markdown({ children }: { children: string }) {
  return (
    <MarkdownToJsx
      // Inlined: only used here. See CLAUDE.md styling note on single-use styles.
      className={css`
        color: ${colors.neutral[900]};
        line-height: 1.5;
        word-break: break-word;

        > :first-child {
          margin-top: 0;
        }
        > :last-child {
          margin-bottom: 0;
        }

        p {
          margin: 0 0 8px;
        }

        h1,
        h2,
        h3,
        h4 {
          margin: 16px 0 8px;
          font-weight: 600;
          line-height: 1.3;
        }
        h1 {
          font-size: 20px;
        }
        h2 {
          font-size: 17px;
        }
        h3 {
          font-size: 15px;
        }
        h4 {
          font-size: 14px;
        }

        ul,
        ol {
          margin: 0 0 8px;
          padding-left: 20px;
        }
        li {
          margin: 2px 0;
        }
        li > p {
          margin: 0;
        }

        strong {
          font-weight: 600;
        }

        a {
          color: ${colors.info[500]};
          text-decoration: underline;
        }

        code {
          font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
          font-size: 0.9em;
          background: ${colors.neutral[100]};
          padding: 1px 4px;
          border-radius: 4px;
        }
        pre {
          margin: 0 0 8px;
          padding: 12px;
          background: ${colors.neutral[100]};
          border-radius: 6px;
          overflow-x: auto;
        }
        pre code {
          background: none;
          padding: 0;
        }

        blockquote {
          margin: 0 0 8px;
          padding-left: 12px;
          border-left: 3px solid ${colors.neutral[300]};
          color: ${colors.neutral[600]};
        }

        hr {
          border: none;
          border-top: 1px solid ${colors.neutral[200]};
          margin: 12px 0;
        }

        table {
          border-collapse: collapse;
          margin: 0 0 8px;
        }
        th,
        td {
          border: 1px solid ${colors.neutral[300]};
          padding: 4px 8px;
          text-align: left;
        }
      `}
      options={{
        overrides: {
          a: { props: { target: '_blank', rel: 'noopener noreferrer' } },
          code: {
            component: ({ children: content, ...props }: React.ComponentPropsWithoutRef<'code'>) => {
              if (typeof content === 'string' && content.startsWith(linkPrefix)) {
                const rest = content.slice(linkPrefix.length);
                const separatorIdx = rest.lastIndexOf('||');
                if (separatorIdx > 0) {
                  const url = rest.slice(0, separatorIdx).trim();
                  const label = rest.slice(separatorIdx + 2);
                  if (url && !/^(javascript|data):/i.test(url)) {
                    return (
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        {label}
                      </a>
                    );
                  }
                }
              }
              return <code {...props}>{content}</code>;
            },
          },
        },
        forceWrapper: true,
        wrapper: 'div',
        // children is streamed LLM output — render any raw HTML as literal text
        // rather than parsing it into elements.
        disableParsingRawHTML: true,
      }}
    >
      {replaceAnchorTags(children) || ''}
    </MarkdownToJsx>
  );
}

function TableArtifactView({ artifact }: { artifact: ChatWithDataTableArtifact }) {
  const planningLinkColIdx = artifact.columns.indexOf('planning_link');

  const columns: {
    title: string;
    dataIndex: string;
    key: string;
    ellipsis: boolean;
    render?: (value: unknown, record: Record<string, unknown>) => React.ReactNode;
  }[] = artifact.columns
    .map((col, idx) => ({
      title: col,
      dataIndex: String(idx),
      key: String(idx),
      ellipsis: true,
    }))
    .filter((_col, idx) => idx !== planningLinkColIdx);

  // When a planning_link column is present, make the first visible column a deep link.
  if (planningLinkColIdx !== -1 && columns.length > 0) {
    columns[0] = {
      ...columns[0],
      render: (value, record) => {
        const url = record[String(planningLinkColIdx)];
        return typeof url === 'string' ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {String(value ?? '')}
          </a>
        ) : (
          String(value ?? '')
        );
      },
    };
  }

  const dataSource = artifact.rows.map((row, rowIdx) => {
    const record: Record<string, unknown> = { key: rowIdx };
    row.forEach((cell, colIdx) => {
      record[String(colIdx)] = cell;
    });
    return record;
  });

  return (
    <div className={artifactContainerStyles}>
      <div className={artifactHeaderStyles}>
        <Typography.Text strong>{artifact.title}</Typography.Text>
        <DownloadButton
          size="small"
          permission={TenantUserPermission.AccessChatDataDownload}
          recordType={artifact.title}
          fileType={artifact.fileType === ChatWithDataTableFileType.Csv ? DownloadFileType.Csv : DownloadFileType.Excel}
          getDownloadData={() =>
            artifact.fileType === ChatWithDataTableFileType.Csv
              ? Promise.resolve({ columns: artifact.columns, rows: artifact.rows })
              : Promise.resolve(tableArtifactToSheet(artifact))
          }
        />
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        size="small"
        pagination={dataSource.length > 10 ? { defaultPageSize: 10 } : false}
        scroll={{ x: true }}
      />
    </div>
  );
}

function ChartArtifactView({ artifact }: { artifact: ChatWithDataChartArtifact }) {
  const { chartType, title, xKey, yKey, data } = artifact;
  const hasLinks = data.some((d) => d.planningLink);
  const handlePointClick = (entry: ChatWithDataChartDataPoint & { payload?: ChatWithDataChartDataPoint }) => {
    const url = entry?.planningLink ?? entry?.payload?.planningLink;
    if (typeof url === 'string') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className={artifactContainerStyles}>
      <div className={artifactHeaderStyles}>
        <Typography.Text strong>{title}</Typography.Text>
        <DownloadButton
          size="small"
          permission={TenantUserPermission.AccessChatDataDownload}
          recordType={title}
          getDownloadData={() => Promise.resolve(chartArtifactToSheet(artifact))}
        />
      </div>
      <ResponsiveContainer width="100%" height={300}>
        {chartType === ChartType.Bar ? (
          <BarChart data={data} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="x" label={{ value: xKey, position: 'insideBottom', offset: -5 }} />
            <YAxis tickFormatter={formatAxisTick} label={yAxisLabel(yKey)} />
            <Tooltip />
            <Bar
              dataKey="y"
              fill={CHART_COLORS[0]}
              onClick={hasLinks ? handlePointClick : undefined}
              cursor={hasLinks ? 'pointer' : undefined}
            />
          </BarChart>
        ) : chartType === ChartType.Line ? (
          <LineChart data={data} margin={CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="x" label={{ value: xKey, position: 'insideBottom', offset: -5 }} />
            <YAxis tickFormatter={formatAxisTick} label={yAxisLabel(yKey)} />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="y"
              stroke={CHART_COLORS[0]}
              activeDot={
                hasLinks
                  ? {
                      onClick: (
                        _e: React.MouseEvent,
                        payload: ChatWithDataChartDataPoint & { payload?: ChatWithDataChartDataPoint },
                      ) => handlePointClick(payload),
                      cursor: 'pointer',
                    }
                  : undefined
              }
            />
          </LineChart>
        ) : (
          <PieChart>
            <Pie
              data={data}
              dataKey="y"
              nameKey="x"
              cx="50%"
              cy="50%"
              outerRadius={100}
              label
              onClick={hasLinks ? handlePointClick : undefined}
              cursor={hasLinks ? 'pointer' : undefined}
            >
              {data.map((_entry, index) => (
                <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

const bulletStyles = css`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  color: ${colors.neutral[500]};
  padding: 2px 0;

  &::before {
    content: '•';
    line-height: 1.5;
  }
`;

const toolBulletStyles = css`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${colors.neutral[500]};
  padding: 2px 0;

  &::before {
    content: '•';
  }
`;

const spinnerStyles = css`
  display: inline-block;
  width: 8px;
  height: 8px;
  border: 1.5px solid ${colors.neutral[400]};
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

function ToolBlockView({ name, status }: { name: ChatWithDataToolName; status: ToolBlockStatus }) {
  const labels = TOOL_LABELS[name];
  const label = labels ? (status === ToolBlockStatus.Running ? labels.running : labels.done) : name;

  return (
    <div className={toolBulletStyles}>
      {status === ToolBlockStatus.Running && <span className={spinnerStyles} />}
      {label}
    </div>
  );
}

function renderBlock(block: ContentBlock): React.ReactNode {
  switch (block.type) {
    case ContentBlockType.Text:
      return <Markdown>{block.text}</Markdown>;
    case ContentBlockType.Table:
      return <TableArtifactView artifact={block.table} />;
    case ContentBlockType.Chart:
      return <ChartArtifactView artifact={block.chart} />;
    case ContentBlockType.MinMaxProposal:
      return <MinMaxProposalCard proposal={block.proposal} />;
    case ContentBlockType.Tool:
      return <ToolBlockView name={block.name} status={block.status} />;
    default:
      return null;
  }
}

function AssistantMessageContent({ message }: { message: ChatMessage }) {
  const blocks = message.blocks ?? [];
  const items: React.ReactNode[] = [];

  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i];

    // Group consecutive ExecuteSql tool blocks into a single label.
    if (block.type === ContentBlockType.Tool && block.name === ChatWithDataToolName.ExecuteSql) {
      let doneCount = 0;
      const groupStart = i;
      while (i < blocks.length) {
        const b = blocks[i];
        if (b.type !== ContentBlockType.Tool || b.name !== ChatWithDataToolName.ExecuteSql) break;
        if (b.status === ToolBlockStatus.Done) doneCount++;
        i++;
      }
      if (doneCount > 0) {
        const label = `${TOOL_LABELS[ChatWithDataToolName.ExecuteSql].done}${doneCount === 1 ? '' : ` × ${doneCount}`}`;
        items.push(
          <div key={`sql-done-${groupStart}`} className={toolBulletStyles}>
            {label}
          </div>,
        );
      }

      // A Running block can only be the last in the group (it's currently executing).
      const lastInGroup = blocks[i - 1];
      const isSqlRunning = lastInGroup.type === ContentBlockType.Tool && lastInGroup.status === ToolBlockStatus.Running;
      if (isSqlRunning) {
        const label = TOOL_LABELS[ChatWithDataToolName.ExecuteSql].running;
        items.push(
          <div key={`sql-run-${groupStart}`} className={toolBulletStyles}>
            <span className={spinnerStyles} />
            {label}
          </div>,
        );
      }
    } else if (
      block.type === ContentBlockType.Tool &&
      HIDDEN_WHEN_DONE_TOOLS.has(block.name) &&
      block.status === ToolBlockStatus.Done
    ) {
      i++;
    } else {
      // Default rendering (text, table/chart artifacts, other tool blocks).
      const isLast = i === blocks.length - 1;
      const view = renderBlock(block);
      if (view) {
        if (!isLast && block.type === ContentBlockType.Text) {
          items.push(
            <div key={i} className={bulletStyles}>
              {view}
            </div>,
          );
        } else {
          items.push(<React.Fragment key={i}>{view}</React.Fragment>);
        }
      }
      i++;
    }
  }

  return <div>{items}</div>;
}

/** Tool blocks that should only be visible while running and hidden once done. */
const HIDDEN_WHEN_DONE_TOOLS = new Set([
  ChatWithDataToolName.ReturnTable,
  ChatWithDataToolName.ReturnChart,
  ChatWithDataToolName.ReadData,
  ChatWithDataToolName.ReturnUploadData,
  ChatWithDataToolName.ProposeMinMaxChanges,
]);

const TOOL_LABELS: Record<ChatWithDataToolName, { running: string; done: string }> = {
  [ChatWithDataToolName.ExecuteSql]: { running: 'Running query...', done: 'Ran query' },
  [ChatWithDataToolName.ReturnTable]: { running: 'Building table...', done: 'Built table' },
  [ChatWithDataToolName.ReturnChart]: { running: 'Building chart...', done: 'Built chart' },
  [ChatWithDataToolName.ReadData]: { running: 'Reading data...', done: 'Read data' },
  [ChatWithDataToolName.ReturnUploadData]: { running: 'Processing upload data...', done: 'Processed upload data' },
  [ChatWithDataToolName.ProposeMinMaxChanges]: { running: 'Preparing min/max proposal...', done: 'Prepared proposal' },
};

function getTypingContent(messages: ChatMessage[]): string | undefined {
  const lastMsg = messages[messages.length - 1];
  if (lastMsg?.role === ChatWithDataMessageRole.Assistant && lastMsg.activeToolName) {
    return TOOL_LABELS[lastMsg.activeToolName]?.running || 'Working...';
  }
  return 'Analyzing your data...';
}

// Shared by both message directions; combined with direction-specific styles via cx().
const messageContentStyles = css`
  .cs-message__content {
    max-width: 100%;
  }
`;

/** Rounded input with a rounded-square send button, shared by the full-page chat and the drawer. */
export function ChatComposer({
  placeholder,
  onSend,
  disabled,
  className,
}: {
  placeholder: string;
  onSend: (text: string, fileData?: ChatWithDataFileData) => void;
  disabled?: boolean;
  className?: string;
}) {
  const [value, setValue] = useState('');
  const [attachedFile, setAttachedFile] = useState<ChatWithDataFileData | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const submit = () => {
    const text = value.trim();
    if (!text || disabled || isParsing) return;
    onSend(text, attachedFile ?? undefined);
    setValue('');
    setAttachedFile(null);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so the same file can be re-selected after removal.
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    setIsParsing(true);
    try {
      const parsed = await parseCsvFile(file);
      setAttachedFile(parsed);
    } catch (err: any) {
      antdMessage.error(err?.message || 'Failed to parse file');
    } finally {
      setIsParsing(false);
    }
  };

  return (
    <div
      className={cx(
        css`
          display: flex;
          flex-direction: column;
          background: ${colors.neutral[0]};
          border: 1px solid ${colors.neutral[300]};
          border-radius: 12px;

          &:focus-within {
            border-color: ${colors.primary[500]};
          }
        `,
        className,
      )}
    >
      {attachedFile && (
        <div
          className={css`
            display: flex;
            align-items: center;
            gap: 6px;
            margin: 8px 16px 0;
            padding: 4px 8px;
            background: ${colors.neutral[100]};
            border-radius: 6px;
            font-size: 12px;
            color: ${colors.neutral[700]};
            width: fit-content;
            max-width: 100%;
          `}
        >
          <FileTextOutlined />
          <span
            className={css`
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            `}
          >
            {attachedFile.fileName}
          </span>
          <button
            type="button"
            className={css`
              all: unset;
              cursor: pointer;
              color: ${colors.neutral[400]};
              flex-shrink: 0;
              display: flex;

              &:hover {
                color: ${colors.neutral[700]};
              }

              &:focus-visible {
                outline: 2px solid ${colors.primary[500]};
                outline-offset: 2px;
                border-radius: 2px;
              }
            `}
            onClick={() => setAttachedFile(null)}
          >
            <CloseOutlined />
          </button>
        </div>
      )}
      <div
        className={css`
          display: flex;
          align-items: flex-end;
          gap: 8px;
          padding: 8px 8px 8px 12px;
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_FILE_EXTENSIONS}
          onChange={handleFileSelect}
          className={css`
            display: none;
          `}
        />
        <button
          type="button"
          className={css`
            flex-shrink: 0;
            width: 32px;
            height: 32px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: none;
            border-radius: 8px;
            font-size: 16px;
            color: ${colors.neutral[500]};
            background: transparent;
            cursor: pointer;
            transition: color 0.15s ease;

            &:hover:not(:disabled) {
              color: ${colors.neutral[800]};
            }
            &:disabled {
              color: ${colors.neutral[300]};
              cursor: default;
            }
          `}
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
        >
          <PaperClipOutlined />
        </button>
        <Input.TextArea
          className={css`
            &.ant-input {
              flex: 1;
              padding: 6px 0;
              border: none;
              box-shadow: none;
              resize: none;
              background: transparent;
            }
          `}
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onPressEnter={(e) => {
            // Enter sends; Shift+Enter inserts a newline.
            if (!e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          autoSize={{ minRows: 1, maxRows: 6 }}
          disabled={disabled}
          bordered={false}
          autoFocus
        />
        <button
          type="button"
          className={css`
            flex-shrink: 0;
            width: 36px;
            height: 36px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: none;
            border-radius: 10px;
            font-size: 16px;
            color: ${colors.neutral[0]};
            background: ${colors.primary[600]};
            cursor: pointer;
            transition: background 0.15s ease;

            &:hover:not(:disabled) {
              background: ${colors.primary[700]};
            }
            &:disabled {
              color: ${colors.neutral[400]};
              background: ${colors.neutral[200]};
              cursor: default;
            }
          `}
          onClick={submit}
          disabled={disabled || isParsing || value.trim().length === 0}
        >
          <ArrowUpOutlined />
        </button>
      </div>
    </div>
  );
}

export interface ChatConversationProps {
  messages: ChatMessage[];
  isLoading: boolean;
  onSend: (text: string, fileData?: ChatWithDataFileData) => void;
  placeholder: string;
  /** Inner content shown centered when there are no messages yet. */
  emptyState?: React.ReactNode;
  /** Optional header rendered above the message list (e.g. the full-page ConversationHeader). */
  header?: React.ReactNode;
}

/**
 * The shared chat body — message list (user/assistant bubbles + inline table/chart
 * artifacts), typing indicator, and input — used by both the full-page chat and the
 * page-aware drawer so the two stay identical.
 */
export function ChatConversation({
  messages,
  isLoading,
  onSend,
  placeholder,
  emptyState,
  header,
}: ChatConversationProps) {
  return (
    // Custom flex column instead of chatscope <ChatContainer> — ChatContainer slots children by
    // matching component types, which would drop our custom header + composer.
    <div
      className={css`
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      `}
    >
      {header}
      <div
        className={css`
          flex: 1;
          min-height: 0;

          .cs-message-list {
            height: 100%;
          }
        `}
      >
        {messages.length === 0 && emptyState ? (
          // Own scroll container: margin:auto centers the content when it fits and, unlike
          // justify-content:center, keeps the top reachable (scrolls) when it's taller than the panel.
          <div
            className={css`
              height: 100%;
              overflow-y: auto;
              display: flex;
              flex-direction: column;
            `}
          >
            <div
              className={css`
                margin: auto;
                width: 100%;
                padding: 32px;
                text-align: center;
                color: ${colors.neutral[500]};
              `}
            >
              {emptyState}
            </div>
          </div>
        ) : (
          <MessageList
            className={css`
              padding: 16px 0;
            `}
            typingIndicator={isLoading ? <TypingIndicator content={getTypingContent(messages)} /> : undefined}
          >
            {messages.map((msg, idx) => {
              if (msg.role === ChatWithDataMessageRole.User) {
                return (
                  <Message
                    key={msg.id}
                    className={cx(
                      messageContentStyles,
                      css`
                        .cs-message__content {
                          background: ${colors.primary[600]};
                          color: ${colors.neutral[0]};
                        }
                      `,
                    )}
                    model={{ direction: 'outgoing', position: 'single' }}
                  >
                    <Message.CustomContent>
                      {msg.fileName && (
                        <div
                          className={css`
                            display: inline-flex;
                            align-items: center;
                            gap: 4px;
                            padding: 2px 8px;
                            margin-bottom: 6px;
                            background: rgba(255, 255, 255, 0.15);
                            border-radius: 4px;
                            font-size: 12px;
                          `}
                        >
                          <FileTextOutlined />
                          {msg.fileName}
                        </div>
                      )}
                      <div>{msg.content}</div>
                    </Message.CustomContent>
                  </Message>
                );
              }

              // While streaming, the last assistant message is shown as the typing indicator.
              if (isLoading && idx === messages.length - 1 && !msg.blocks?.length) {
                return null;
              }

              return (
                <Message
                  key={msg.id}
                  className={cx(
                    messageContentStyles,
                    css`
                      &.cs-message {
                        max-width: 90%;
                      }
                      /* content-wrapper must get min-width: 0 to shrink to the 90% cap instead
                     of expanding to its content's intrinsic width and being clipped. */
                      .cs-message__content-wrapper {
                        min-width: 0;
                      }
                      .cs-message__content {
                        min-width: 0;
                        overflow-x: auto;
                        background: ${colors.neutral[100]};
                        color: ${colors.neutral[900]};
                      }
                    `,
                  )}
                  model={{ direction: 'incoming', position: 'single' }}
                >
                  <Message.CustomContent>
                    <AssistantMessageContent message={msg} />
                  </Message.CustomContent>
                </Message>
              );
            })}
          </MessageList>
        )}
      </div>
      <ChatComposer
        placeholder={placeholder}
        onSend={onSend}
        disabled={isLoading}
        className={css`
          margin: 8px 16px 16px;
        `}
      />
    </div>
  );
}
